(function () {
  const ACCOUNT_FIRST_TARGET_TYPES = new Set(["product", "artist", "label"]);

  function bindRevealButtons(root) {
    const buttons = (root || document).querySelectorAll(".notification-signup-toggle");
    buttons.forEach((button) => {
      if (button.dataset.notifyRevealBound === "true") {
        return;
      }
      button.dataset.notifyRevealBound = "true";
      button.addEventListener("click", async function () {
        const form = button.closest(".notification-signup-form");
        const panel = form ? form.querySelector(".notification-signup-panel") : null;
        const emailInput = panel ? panel.querySelector('input[type="email"]') : null;
        const messageNode = form ? form.querySelector(".notification-signup-message") : null;

        if (form && form.dataset.targetType === "product") {
          button.disabled = true;
          setMessage(messageNode, "", "");
          try {
            const subscribed = await tryAccountSubscribe(form);
            if (subscribed) {
              showSuccess(form, messageNode, button, "You are subscribed.");
              return;
            }
          } catch (error) {
            // Fall through to the email field when the storefront cannot prove customer identity.
          } finally {
            if (form.dataset.notifySubscribed !== "true") {
              button.disabled = false;
            }
          }
        }

        if (!panel) {
          return;
        }

        form.classList.add("is-revealed");
        form.dataset.accountFallbackRevealed = "true";
        panel.hidden = false;
        button.hidden = true;
        button.setAttribute("aria-expanded", "true");
        if (emailInput) {
          emailInput.focus();
        }
      });
    });
  }

  function bindForms(root) {
    const forms = (root || document).querySelectorAll(".notification-signup-form");
    forms.forEach((form) => {
      if (form.dataset.notifyBound === "true") {
        return;
      }
      form.dataset.notifyBound = "true";
      prepareAccountFirstForm(form);
      form.addEventListener("submit", onSubmit);
    });
  }

  async function onSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const endpoint = form.dataset.endpoint || "";
    const targetType = form.dataset.targetType || "";
    const targetLabel = form.dataset.targetLabel || "";
    const targetValue = form.dataset.targetValue || "";
    const productGid = form.dataset.productGid || "";
    const emailInput = form.querySelector('[name="email"]');
    const messageNode = form.querySelector(".notification-signup-message");
    const submitButton = form.querySelector('button[type="submit"]');

    if (!endpoint || !emailInput || !submitButton) {
      return;
    }

    setMessage(messageNode, "", "");
    submitButton.disabled = true;

    if (canUseAccountSubscribe(form) && form.dataset.accountFallbackRevealed !== "true") {
      try {
        const subscribed = await tryAccountSubscribe(form);
        if (subscribed) {
          showSuccess(form, messageNode, submitButton, "You are subscribed.");
          return;
        }
        revealEmailFallback(form, emailInput);
        return;
      } catch (error) {
        setMessage(messageNode, error.message || "Something went wrong. Please try again.", "error");
        return;
      } finally {
        if (form.dataset.notifySubscribed !== "true") {
          submitButton.disabled = false;
        }
      }
    }

    if (emailInput.type === "email" && !emailInput.value) {
      revealEmailFallback(form, emailInput);
      if (typeof emailInput.reportValidity === "function") {
        emailInput.reportValidity();
      }
      submitButton.disabled = false;
      return;
    }

    const payload = buildSubscriptionPayload(form, emailInput.value);

    try {
      const response = await fetch(endpoint.replace(/\/+$/, "") + "/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.error || "Request failed.");
      }

      if (emailInput.type === "email") {
        emailInput.value = "";
      }
      if (data.status === "already_subscribed") {
        showSuccess(form, messageNode, submitButton, "You are subscribed.");
      } else {
        showSuccess(form, messageNode, submitButton, "You are subscribed.");
      }
    } catch (error) {
      setMessage(messageNode, error.message || "Something went wrong. Please try again.", "error");
    } finally {
      if (form.dataset.notifySubscribed !== "true") {
        submitButton.disabled = false;
      }
    }
  }

  function prepareAccountFirstForm(form) {
    if (!form || form.dataset.targetType === "product" || !canUseAccountSubscribe(form)) {
      return;
    }

    const emailInput = form.querySelector('input[type="email"][name="email"]');
    if (!emailInput) {
      return;
    }

    if (!emailInput.dataset.notifyOriginalRequired) {
      emailInput.dataset.notifyOriginalRequired = emailInput.required ? "true" : "false";
    }

    const wrapper = emailInput.closest(".field") || emailInput;
    wrapper.hidden = true;
    emailInput.required = false;
  }

  function revealEmailFallback(form, emailInput) {
    if (!form || !emailInput || emailInput.type !== "email") {
      return;
    }

    const wrapper = emailInput.closest(".field") || emailInput;
    wrapper.hidden = false;
    form.dataset.accountFallbackRevealed = "true";
    if (emailInput.dataset.notifyOriginalRequired === "true") {
      emailInput.required = true;
    }
    emailInput.focus();
  }

  function canUseAccountSubscribe(form) {
    const targetType = form ? form.dataset.targetType || "" : "";
    if (!ACCOUNT_FIRST_TARGET_TYPES.has(targetType)) {
      return false;
    }
    if (targetType === "product" && !form.dataset.productGid) {
      return false;
    }
    return true;
  }

  function buildSubscriptionPayload(form, email) {
    const targetType = form.dataset.targetType || "";
    const productGid = form.dataset.productGid || "";
    const payload = {
      email: email || "",
      target_type: targetType,
      target_label: form.dataset.targetLabel || "",
    };

    if (targetType === "product") {
      payload.product_gid = productGid;
      payload.target_key = productGid;
    } else {
      payload.target_value = form.dataset.targetValue || "";
    }

    return payload;
  }

  async function tryAccountSubscribe(form) {
    const response = await fetch("/apps/wantlist/subscribe", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(buildSubscriptionPayload(form, "")),
    });

    if (response.status === 401 || response.status === 403 || response.status === 409) {
      return false;
    }

    const data = await response.json();
    if (!response.ok || !data.ok) {
      throw new Error(data.error || "Request failed.");
    }
    return true;
  }

  function setMessage(node, text, status) {
    if (!node) {
      return;
    }
    node.textContent = text;
    node.dataset.status = status || "";
  }

  function showSuccess(form, messageNode, submitButton, text) {
    const cardForm = form && (
      form.classList.contains("notification-signup--card")
      || form.closest(".notification-signup--card")
    );
    const productForm = form && form.dataset.targetType === "product";

    if (cardForm || productForm) {
      const panel = form.querySelector(".notification-signup-panel");
      const toggle = form.querySelector(".notification-signup-toggle");
      const button = toggle || submitButton;

      if (panel) {
        panel.hidden = true;
      }
      form.classList.remove("is-revealed");
      if (button) {
        button.hidden = false;
        button.disabled = true;
        button.setAttribute("aria-expanded", "false");
        setButtonText(button, text);
      }
      form.dataset.notifySubscribed = "true";
      setMessage(messageNode, "", "success");
      return;
    }

    setMessage(messageNode, text, "success");
  }

  function setButtonText(button, text) {
    const label = button.querySelector("span") || button;
    label.textContent = text;
  }

  function bind(root) {
    bindRevealButtons(root || document);
    bindForms(root || document);
  }

  window.FurtherNotificationSignup = window.FurtherNotificationSignup || {};
  window.FurtherNotificationSignup.bind = bind;

  document.addEventListener("DOMContentLoaded", function () {
    bind(document);
  });

  document.addEventListener("shopify:section:load", function (event) {
    bind(event.target);
  });
})();
